"use client"

import { useActionState, useEffect } from "react";
import { createContact } from "./actions";
import { State, initialState } from "@/types/forms";
import { Form, Input, TextArea } from "@/app/ui/molecular/formFields";
import { useFormDraft } from "@/app/lib/hooks/useFormDraft";

type ContactDraft = {
  email: string;
  name: string;
  message: string;
};

export default function ContactForm() {
  const [state, formAction] = useActionState<State, FormData>(createContact, initialState);

  const { values, updateField, saveNow, clearDraft, hasDraft, lastSaved } = useFormDraft<ContactDraft>(
    "contact_form",
    { email: "", name: "", message: "" }
  );

  useEffect(() => {
    if (state.message === "SUCCESS") {
      clearDraft();
    } else if (state.errors) {
      saveNow();
    }
  }, [state.message, state.errors, clearDraft, saveNow]);

  if (state.message === "DEPLOYMENT_MISMATCH") {
    return (
      <div className="w-full max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-6 sm:py-8 lg:py-12">
        <div className="relative rounded-2xl shadow-xl overflow-hidden p-8 text-center border border-white/10 bg-gradient-to-b from-(--purple)/95 to-(--dark-purple)">
          <p className="text-white text-lg mb-4">
            This page has been updated since you last visited.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="font-bold rounded-full py-3 px-8 text-lg transition-all hover:scale-105 text-(--dark-purple) bg-gradient-to-r from-(--brand) to-(--gold) hover:opacity-90"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <Form
      name="Contact"
      action={formAction}
      error={state.message && state.message !== "" && !state.message.includes("DEPLOYMENT") ? state.message : undefined}
      hasDraft={hasDraft}
      lastSaved={lastSaved}
      onClearDraft={clearDraft}
    >

      <Input
        id="email"
        type="email"
        label="Email:"
        placeholder="Enter email"
        maxLength={100}
        required
        autoComplete="email"
        errors={state.errors?.email}
        defaultValue={values.email}
        onChange={(e) => updateField("email", e.target.value)}
      />

      <Input
        id="name"
        type="text"
        label="Name:"
        placeholder="Enter name"
        maxLength={40}
        required
        autoComplete="name"
        filterInput={(v) => v.replace(/[^a-zA-ZÀ-ÿ\s'\-\.]/g, "")}
        errors={state.errors?.name}
        defaultValue={values.name}
        onChange={(e) => updateField("name", e.target.value)}
      />

      <TextArea
        id="message"
        label="Message:"
        placeholder="Enter message"
        maxLength={750}
        required
        errors={state.errors?.message}
        defaultValue={values.message}
        onChange={(e) => updateField("message", e.target.value)}
      />
    </Form>
  );
}
